import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import config from '../config'
import { logger } from '../utils/dccLogger'
import { E3DGrpAuthState } from '../redux/reducers/e3DGrpAuthReducer'
import { clearPersistedE3DGrpAuth, persistE3DGrpAuth } from '../redux/persistLocalStorageE3DAuthState'

export type TAuthToken = {
    idToken: string
    accessToken: string
    refreshToken: string
    expiresAt: number
}

export function setAuthToken(user: User): void {
    try {
        const { id_token, access_token, refresh_token, expires_at } = user
        if (!id_token || !access_token || !refresh_token || !expires_at) {
            throw new Error('Provided user is not logged in. Cannot set tokens.')
        }

        const token: TAuthToken = {
            idToken: id_token,
            accessToken: access_token,
            refreshToken: refresh_token,
            expiresAt: expires_at,
        }

        sessionStorage.setItem('auth:token', JSON.stringify(token))
        localStorage.setItem('auth:token', JSON.stringify(token))
    } catch (error) {
        logger.error(`Error storing auth tokens: ${error}`)
    }
}

export class AuthTokenStore implements Storage {
    private storage: Storage

    constructor(storage: Storage) {
        this.storage = storage
    }

    setItem(key: string, value: string): void {
        this.storage.setItem(key, value)

        if (key.startsWith('oidc.user:')) {
            try {
                const userData = JSON.parse(value)
                const user = new User(userData)
                setAuthToken(user)
            } catch (error) {
                logger.error(`Error processing auth data: ${error}`)
            }
        }
    }

    getItem(key: string): string | null {
        return this.storage.getItem(key)
    }

    removeItem(key: string): void {
        this.storage.removeItem(key)

        if (key.startsWith('oidc.user:')) {
            sessionStorage.removeItem('auth:token')
            localStorage.removeItem('auth:token')
        }
    }

    clear(): void {
        this.storage.clear()
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    get length(): number {
        return this.storage.length
    }

    key(index: number): string | null {
        return this.storage.key(index)
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function verifyAuthToken(input: any): input is TAuthToken {
    return (
        input &&
        typeof input?.idToken === 'string' &&
        typeof input?.accessToken === 'string' &&
        typeof input?.refreshToken === 'string' &&
        typeof input?.expiresAt === 'number'
    )
}

function getRedirectUri(): string {
    return window.location.origin
}

function isInIframe(): boolean {
    try {
        return window.self !== window.top
    } catch {
        return true
    }
}

export default function getOAuthClient(): UserManager | null {
    const authority = config.E3DEX_GRP_AUTH_DOMAIN_URL
    const clientId = config.E3DEX_GRP_CLIENT_ID

    if (!authority || !clientId) {
        logger.error('Authentication configuration missing: authority or clientId')
        return null
    }

    const redirectUri = getRedirectUri()

    return new UserManager({
        authority: authority,
        client_id: clientId,
        redirect_uri: redirectUri,
        popup_redirect_uri: redirectUri,
        revokeTokenTypes: ['refresh_token'],
        automaticSilentRenew: !isInIframe(),
        userStore: new WebStorageStateStore({ store: new AuthTokenStore(sessionStorage) }),
        metadata: {
            authorization_endpoint: `${authority}/oauth2/authorize`,
            response_types_supported: ['code', 'token'],
            scopes_supported: ['openid', 'email', 'phone', 'profile'],
            subject_types_supported: ['public'],
            token_endpoint: `${authority}/oauth2/token`,
            token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post'],
            userinfo_endpoint: `${authority}/oauth2/userInfo`,
            end_session_endpoint: `${authority}/logout?client_id=${clientId}&logout_uri=${redirectUri}`,
        },
    })
}

export function cleanupExpiredTokens(): void {
    try {
        logger.info('Cleaning up expired tokens from storage')
        sessionStorage.removeItem('auth:token')
        localStorage.removeItem('auth:token')

        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('oidc.user:')) {
                sessionStorage.removeItem(key)
            }
        })

        Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('oidc.user:')) {
                localStorage.removeItem(key)
            }
        })

        logger.info('Token cleanup completed')
    } catch (error) {
        logger.error(`Error cleaning up expired tokens: ${error}`)
    }
}

export function isAuthenticated(): boolean {
    try {
        let authData = sessionStorage.getItem('auth:token')

        if (!authData) {
            authData = localStorage.getItem('auth:token')
        }

        if (!authData) {
            return false
        }

        const token = JSON.parse(authData)
        if (!verifyAuthToken(token)) {
            return false
        }

        const expiresAtMs = token.expiresAt * 1000
        const graceMs = 5000
        const expiresAt = new Date(expiresAtMs - graceMs)
        const currentDate = new Date()

        const isValid = expiresAt > currentDate

        return isValid
    } catch (error) {
        logger.error(`Error checking authentication status: ${error}`)
        return false
    }
}

export async function logout(): Promise<void> {
    if (window?.E3D?.session) {
        window.E3D.endSession().then(() => logger.info('E3D session ended successfully'))
    }

    try {
        const userClient = getOAuthClient()
        if (!userClient) {
            throw new Error('OAuth client not available')
        }

        await userClient.removeUser()
        await userClient.clearStaleState()

        sessionStorage.removeItem('auth:token')
        localStorage.removeItem('auth:token')

        logger.info('Logout successful, redirecting to IDP logout')
        await userClient.signoutRedirect()
        clearPersistedE3DGrpAuth()
    } catch (error) {
        logger.error(`Error during logout: ${error}`)
        window.location.href = '/'
    }
}

export async function refreshTokens(
    userClient: UserManager,
    user: User,
    oldGrpAuthState: E3DGrpAuthState,
): Promise<{ success: boolean; updatedUser?: User; terminal?: boolean }> {
    if (!user.refresh_token || !userClient) {
        logger.warn("OAuth token expired and can't refresh it")
        return { success: false }
    }

    try {
        const authority = config.E3DEX_GRP_AUTH_DOMAIN_URL
        const clientId = config.E3DEX_GRP_CLIENT_ID
        const tokenEndpoint = `${authority}/oauth2/token`

        if (!authority || !clientId) {
            throw new Error('Authentication configuration missing: authority or clientId')
        }

        const refreshRequestBody = new URLSearchParams()
        refreshRequestBody.set('grant_type', 'refresh_token')
        refreshRequestBody.set('client_id', clientId)
        refreshRequestBody.set('refresh_token', user.refresh_token)

        const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: refreshRequestBody.toString(),
        })

        if (!response.ok) {
            if (response.status >= 400 && response.status < 500) {
                throw new Error('invalid_grant') // Treat 4xx errors as terminal grant failures
            }
            throw new Error(`Refresh token request failed with status: ${response.status}`)
        }

        const result = await response.json()
        const expiresAt = Math.floor(Date.now() / 1000) + result.expires_in

        const updatedUser = new User({
            ...(user.toStorageString ? JSON.parse(user.toStorageString()) : user),
            access_token: result.access_token,
            refresh_token: result.refresh_token ?? user.refresh_token,
            id_token: result.id_token ?? user.id_token,
            expires_at: expiresAt,
            token_type: result.token_type ?? user.token_type,
            scope: result.scope ?? user.scope,
        })

        setAuthToken(updatedUser)

        const newGrpAuthState = {
            ...oldGrpAuthState,
            user: updatedUser,
        }

        persistE3DGrpAuth(newGrpAuthState)

        logger.info('OAuth tokens refreshed successfully via fetch')
        return { success: true, updatedUser, terminal: false }
    } catch (error: any) {
        const errorMessage = error?.message || String(error)
        logger.error(`Failed to refresh tokens: ${errorMessage}`)

        const lowerMessage = errorMessage.toLowerCase()
        const terminalErrors = ['login_required', 'interaction_required', 'consent_required', 'invalid_grant']
        const isTerminalError = terminalErrors.some((err) => lowerMessage.includes(err))

        if (isTerminalError) {
            logger.warn('Terminal OAuth error detected during token refresh')
        } else {
            logger.info(
                'Non-terminal refresh error (e.g. transient network issue) - preserving session for next attempt',
            )
        }

        return { success: false, terminal: isTerminalError }
    }
}
